import type { ReactNode } from "react";

import { cn } from "@/shared/lib/utils";

/** Semantic tone for a pill — drives color as SIGNAL, never decoration. */
export type PillTone = "neutral" | "brand" | "success" | "warning";

interface PillProps {
	/** Tone selects the tint. Defaults to neutral (muted). */
	tone?: PillTone;
	/** Optional leading icon node (e.g. a lucide icon already sized + aria-hidden). */
	icon?: ReactNode;
	children: ReactNode;
	className?: string;
}

// Tone → tinted border + faint fill + colored text. `brand` (the purple `--primary`
// accent) is reserved for live/affirmative signals; success mint and warning amber
// mirror StatusChip so the bespoke docs read as one family with the compliance grid.
const TONE_CLASS: Record<PillTone, string> = {
	neutral: "border-border bg-muted/50 text-muted-foreground",
	brand: "border-primary/30 bg-primary/10 text-primary",
	success: "border-success/30 bg-success/10 text-success",
	warning: "border-warning/30 bg-warning/10 text-warning",
};

/** Compact label pill (tag/keyword) with a tinted, tone-driven treatment. */
export function Pill({ tone = "neutral", icon, children, className }: PillProps) {
	return (
		<span
			className={cn(
				"inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-semibold",
				TONE_CLASS[tone],
				className,
			)}
		>
			{icon}
			{children}
		</span>
	);
}
